import { Button, Checkbox, UploadFilledIcon } from '@calizahq/react-components-v2'
import { Editor } from '@shared/components/Editor/Editor'
import { getGroupMentionsValues, getMentionsValues } from '@shared/components/Editor/util/Mentions.util'
import clsx from 'clsx'
import { Form, Formik, useField } from 'formik'
import { $getRoot, EditorState } from 'lexical'
import React, { FC, useCallback, useMemo, useRef, useState } from 'react'
import * as Yup from 'yup'

import { useAlertContext, useUserContext } from '@/context'
import { TicketCommentMutations } from '@/graphql/mutations/ticketComment.mutations'
import { useGraphqlMutation } from '@/hooks/useGraphql'

type FormSchema = { attachments?: File[]; internalOnly: boolean; text: string }

type Props = {
  activeCommentToReply: number | undefined
  refetchOnSuccess?: () => void
  setActiveCommentToReply: (commentId?: number) => void
  ticketId: number
}

const intialValues: FormSchema = { internalOnly: true, attachments: undefined, text: '' }

export const CreateComment: FC<Props> = ({
  ticketId,
  setActiveCommentToReply,
  activeCommentToReply,
  refetchOnSuccess,
}) => {
  // Use ref instead of state to avoid unnecessary re-renders
  const commentRef = useRef<EditorState | undefined>(undefined)
  const [successCount, setSuccessCount] = useState<number>(0)
  const resetFormRef = useRef<(() => void) | null>(null)
  const setFieldValueRef = useRef<((field: string, value: any) => void) | null>(null)
  const { setAlert } = useAlertContext()
  const { user } = useUserContext()

  // Stable handler for editor changes - memoized to prevent re-renders
  const handleEditorChange = useCallback((editorState: EditorState) => {
    commentRef.current = editorState
    const textContent = editorState.read(() => $getRoot().__cachedText) || ''
    if (setFieldValueRef.current) {
      setFieldValueRef.current('text', textContent)
    }
  }, [])

  // Memoize mutation config to prevent recreating callbacks on every render
  const mutationConfig = useMemo(
    () => ({
      onSuccess: () => {
        setAlert('Created new Comment!', 'success')
        if (refetchOnSuccess) refetchOnSuccess()
        setActiveCommentToReply(undefined)
        setSuccessCount((prev) => prev + 1)
        if (resetFormRef.current) {
          resetFormRef.current()
          resetFormRef.current = null
        }
      },
      onError: (error: any) => {
        const statusCode = error?.response?.status || error?.status || error?.statusCode

        if (statusCode === 413) {
          setAlert('File size is too large. Please reduce the size of your attachment and try again.', 'error')
          return
        }

        const errorMessage =
          error?.response?.errors?.[0]?.message || error?.errors?.[0]?.message || error?.message || null

        if (errorMessage && errorMessage.trim()) {
          let displayMessage = 'Failed to send message. Please try again.'

          if (errorMessage.includes('API error')) {
            displayMessage = 'Failed to send message to external platform. Please try again or contact support.'
          } else if (errorMessage.includes('missing external_id')) {
            displayMessage = 'Cannot send message: Missing conversation details.'
          } else if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
            displayMessage = 'Network error. Please check your connection and try again.'
          }

          setAlert(displayMessage, 'error')
        } else {
          setAlert('Failed to send message. Please try again.', 'error')
        }
      },
    }),
    [setAlert, refetchOnSuccess, setActiveCommentToReply],
  )

  const { mutate, isPending: isMutationLoading } = useGraphqlMutation(
    TicketCommentMutations.createTicketComment(),
    mutationConfig,
  )

  const handleSubmit = useCallback(
    ({ attachments, text, ...commentValues }: FormSchema) => {
      const comment = commentRef.current
      const mentionUserIds = getMentionsValues(comment?._nodeMap)
      const mentionGroupIds = getGroupMentionsValues(comment?._nodeMap)
      const richText = JSON.stringify(comment?.toJSON())
      const isEmpty = !text.trim().length

      if (!isEmpty) {
        mutate({
          comment: {
            createdBy: user?.id,
            ticketId,
            mentionUserIds: mentionUserIds.length ? mentionUserIds : null,
            mentionGroupIds: mentionGroupIds.length ? mentionGroupIds : null,
            richText,
            text,
            parentTicketCommentId: activeCommentToReply ? activeCommentToReply : null,
            ...commentValues,
          },
          attachments,
        })
      }
    },
    [mutate, user?.id, ticketId, activeCommentToReply],
  )

  const commentStyle = 'flex w-full flex-col flex-wrap rounded-lg px-4'
  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        ...intialValues,
      }}
      onSubmit={(values, { resetForm }) => {
        // Store resetForm function to call it only on success
        resetFormRef.current = () => resetForm({ values: { ...intialValues } })
        handleSubmit(values)
      }}
      validateOnBlur={false}
      validateOnChange={true}
      validationSchema={formSchema}
    >
      {({ values, errors, setFieldValue, isValid }) => {
        const { attachments, internalOnly } = values

        // Store setFieldValue in ref for the stable handler to use
        setFieldValueRef.current = setFieldValue

        return (
          <>
            <Editor
              activeCommentToReply={activeCommentToReply}
              setComment={handleEditorChange}
              successCount={successCount}
            />
            <Form className={clsx(commentStyle, internalOnly ? 'border-secondary' : 'border-action-primary')}>
              <div className="flex flex-col items-center justify-between md:flex-row ">
                <div className="mb-2 flex w-full items-center justify-between md:mb-0 md:w-auto md:justify-start">
                  <FileUploadComponent name="attachments" />
                  <Checkbox className="" id="internalOnly" label="Post Internally" name="internalOnly" />
                </div>
                {errors['text'] && attachments && attachments.length > 0 && (
                  <p className="mt-2 font-medium text-label-sm text-semantic-error">{errors['text']}</p>
                )}
                <Button
                  className="w-full bg-text-semantic-success md:w-auto"
                  disabled={isMutationLoading || !isValid}
                  size={'small'}
                  type="submit"
                >
                  {isMutationLoading ? 'Sending...' : internalOnly ? 'Post Internally' : 'Send to Member'}
                </Button>
              </div>
              {attachments && attachments?.length > 0 && (
                <p className="text-p-sm">{attachments.length} files attached</p>
              )}
            </Form>
          </>
        )
      }}
    </Formik>
  )
}

function FileUploadComponent({ name }: { name: string }) {
  const [, , { setValue }] = useField<(File | { id: number; src: string })[]>({
    type: 'file',
    name,
  })

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files) {
      return
    }
    setValue([...e.target.files])
  }

  return (
    <div className="flex">
      <label className=" mr-4 text-label-sm leading-120" htmlFor={name}>
        <UploadFilledIcon className="text-grey-800" width={24} />
      </label>
      <input
        accept="image/*,application/pdf"
        className="hidden"
        id={name}
        multiple={true}
        onChange={handleFileChange}
        type="file"
      ></input>
    </div>
  )
}

const formSchema = Yup.object({
  text: Yup.string().test(
    'text-required',
    'Please add text to your comment when including attachments',
    function (value) {
      const { attachments } = this.parent
      const hasText = !!(value && value.trim().length > 0)

      // Always require text for form to be valid, but only show error when attachments present
      if (attachments && attachments.length > 0 && !hasText) {
        return this.createError({ message: 'Please add text to your comment when including attachments' })
      }

      // Still require text for validity (button disabled), but don't show error
      return hasText
    },
  ),
})
